// Resizable — shadcn-style aliases over react-resizable-panels.
//
// Why this file exists alongside Surface.tsx:
//
//   * Surface / Pane (in Surface.tsx) is the high-level primitive used for
//     terminal-page layouts (e.g. /terminal's 4-pane grid). It owns its own
//     pane chrome (24px header, collapse toggle, etc.) and auto-inserts
//     separators between panes.
//   * Resizable (this file) is the lower-level primitive used inside other
//     primitives (e.g. EntityScreen's filter-rail vs result-column split).
//     It maps 1:1 onto react-resizable-panels' Group / Panel / Separator
//     under shadcn-style names so consumers who reach for the more familiar
//     shadcn vocabulary find what they expect.
//
// Both surfaces share the same `[data-separator]` styling in tokens.css —
// hairline at rest, 4-6px hit-zone, accent on hover/focus/drag, col-resize
// cursor — and the same library keyboard nav (Arrow / Home / End on focus).
//
// Persistence. Pass `autoSaveId` and we wire up react-resizable-panels v4's
// `useDefaultLayout({ id })` hook (defaults to localStorage). Per CLAUDE.md,
// shell-level panes namespace under `eid:shell:panes:v1.<surface>` so a
// layout-schema bump is a key rename, not a data migration.

import { useId } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import {
  Group,
  type GroupImperativeHandle,
  Panel,
  Separator,
  useDefaultLayout,
} from 'react-resizable-panels';

// ── ResizablePanelGroup ────────────────────────────────────────────────────
// Horizontal or vertical Group. `autoSaveId` is the persistence key — pass
// the versioned shell key (`eid:shell:panes:v1.<surface>`) and the library
// reads/writes layout to localStorage automatically via useDefaultLayout.

export interface ResizablePanelGroupProps {
  /** Drag axis. */
  direction: 'horizontal' | 'vertical';
  /**
   * Persistence key. When omitted, layout is not persisted (a per-mount
   * useId fallback keeps the underlying hook happy without polluting
   * localStorage).
   */
  autoSaveId?: string;
  /** Imperative handle for programmatic resets. */
  groupRef?: { current: GroupImperativeHandle | null };
  /** Optional className for the group root. */
  className?: string;
  style?: CSSProperties;
  /** `<ResizablePanel>` / `<ResizableHandle>` children. */
  children: ReactNode;
}

export function ResizablePanelGroup({
  direction,
  autoSaveId,
  groupRef,
  className,
  style,
  children,
}: ResizablePanelGroupProps) {
  // Hooks must run unconditionally — useId fills in when no autoSaveId is
  // provided so the library hook doesn't bail.
  const fallbackId = useId();
  const layout = useDefaultLayout({ id: autoSaveId ?? fallbackId });

  return (
    <Group
      orientation={direction}
      groupRef={groupRef}
      defaultLayout={autoSaveId ? layout.defaultLayout : undefined}
      onLayoutChanged={autoSaveId ? layout.onLayoutChanged : undefined}
      className={className}
      style={{ flex: 1, minHeight: 0, minWidth: 0, ...style }}
      data-surface
    >
      {children}
    </Group>
  );
}

// ── ResizablePanel ─────────────────────────────────────────────────────────
// A single panel within a Group. `defaultSize`, `minSize`, `maxSize` are all
// percentages of the parent Group's main axis (0..100).
//
// CRITICAL — react-resizable-panels v4 unit interpretation. Numeric sizes
// passed to the underlying <Panel> are interpreted as PIXELS, not percent.
// Our wrapper's documented contract is "0..100 percent", so we explicitly
// stringify with a `%` suffix before forwarding. Without this, a rail with
// `maxSize={35}` (intended: 35% cap) clamps to 35 pixels, which on a 1000px
// group renders the rail at ~3.3% of the group — completely unreadable.

const toPctString = (n: number | undefined): string | undefined =>
  typeof n === 'number' ? `${n}%` : undefined;

export interface ResizablePanelProps {
  /** Stable id (required for persistence). */
  id: string;
  defaultSize?: number;
  minSize?: number;
  maxSize?: number;
  collapsible?: boolean;
  collapsedSize?: number;
  /** Inline style passed through to the panel content wrapper. */
  style?: CSSProperties;
  className?: string;
  children?: ReactNode;
}

export function ResizablePanel({
  id,
  defaultSize,
  minSize,
  maxSize,
  collapsible,
  collapsedSize,
  style,
  className,
  children,
}: ResizablePanelProps) {
  return (
    <Panel
      id={id}
      defaultSize={toPctString(defaultSize)}
      minSize={toPctString(minSize)}
      maxSize={toPctString(maxSize)}
      collapsible={collapsible}
      collapsedSize={toPctString(collapsedSize)}
      className={className}
    >
      {/* Panel from react-resizable-panels doesn't honor display/overflow
          style overrides on its own element, so the layout wrapper has to
          live inside. Matches the Surface/Pane pattern in Surface.tsx. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          minHeight: 0,
          minWidth: 0,
          overflow: 'hidden',
          ...style,
        }}
      >
        {children}
      </div>
    </Panel>
  );
}

// ── ResizableHandle ────────────────────────────────────────────────────────
// The draggable separator between two ResizablePanels. Picks up cursor + grip
// + hover styling from the global `[data-separator]` block in tokens.css.
// react-resizable-panels handles the actual drag, cursor-capture, and the
// Arrow / Home / End keyboard nav on focus.

export interface ResizableHandleProps {
  /** Accessibility label exposed via the separator's title/aria. */
  'aria-label'?: string;
  /** Disable resizing (still keyboard-focusable, but no drag). */
  disabled?: boolean;
}

export function ResizableHandle({
  'aria-label': ariaLabel = 'Resize panel',
  disabled,
}: ResizableHandleProps = {}) {
  return <Separator aria-label={ariaLabel} disabled={disabled} />;
}

export type { GroupImperativeHandle as ResizableGroupHandle } from 'react-resizable-panels';
